import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Layout,
  Button,
  Space,
  Divider,
  Typography,
  Row,
  Col,
  Card,
  Radio,
  Select
} from 'antd';
import {
  ArrowLeftOutlined,
  ClearOutlined,
  CheckCircleOutlined,
  FilePptOutlined,
  FilePdfOutlined
} from '@ant-design/icons';
import styles from './ReportActions.module.css';

const { Title, Text, Paragraph } = Typography;

/**
 * ReportActions - Step 3 of wizard: Finalize and export report
 *
 * Displays success message and export options (PowerPoint, PDF).
 *
 * Extracted from ReportWriterV2 to improve modularity
 */
const ReportActions = ({
  onSetCurrentStep,
  onNewReport,
  onExportPowerPoint,
  onExportPDF
}) => {
  const [slideSize, setSlideSize] = useState('16:9'); // Default to Widescreen (PowerPoint)
  const [pdfFormat, setPdfFormat] = useState('memo'); // memo, slide-16:9, slide-4:3
  return (
    <Layout className={styles.layout}>
      {/* Top Bar */}
      <div className={styles.topBar}>
        <Space className={styles.topBarSpace}>
          <Space>
            <Button size="small" icon={<ArrowLeftOutlined />} onClick={() => onSetCurrentStep(2)}>
              Back to Edit
            </Button>
            <Divider type="vertical" />
            <Text strong>Finalize and Save</Text>
          </Space>
          <Space>
            <Button danger size="small" icon={<ClearOutlined />} onClick={onNewReport}>
              Clear & New Report
            </Button>
          </Space>
        </Space>
      </div>

      {/* Content */}
      <div className={styles.contentWrapper}>
        <Space direction="vertical" size="large" className={styles.verticalSpace}>
          <div className={styles.successMessage}>
            <CheckCircleOutlined className={styles.successIcon} />
            <Title level={2}>Report Ready</Title>
            <Paragraph type="secondary" className={styles.description}>
              Your report is ready. Export it in your preferred format.
            </Paragraph>
          </div>

          <Divider>Export Options</Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Card hoverable className={styles.cardFullHeight}>
                <Space direction="vertical" className={styles.spaceFullWidth} size="middle">
                  <div>
                    <Text strong className={styles.labelText}>Slide Size:</Text>
                    <Select
                      value={slideSize}
                      onChange={(value) => setSlideSize(value)}
                      className={styles.selectFullWidth}
                      options={[
                        { value: '16:9', label: 'Widescreen (16:9)' },
                        { value: '4:3', label: 'Standard (4:3)' }
                      ]}
                    />
                  </div>
                  <Button
                    type="primary"
                    size="large"
                    icon={<FilePptOutlined />}
                    onClick={() => onExportPowerPoint(slideSize)}
                    block
                  >
                    Export PowerPoint
                  </Button>
                  <Text type="secondary" className={styles.exportText}>
                    Download as .pptx presentation
                  </Text>
                </Space>
              </Card>
            </Col>
            <Col span={12}>
              <Card hoverable className={styles.cardFullHeight}>
                <Space direction="vertical" className={styles.spaceFullWidth} size="middle">
                  <div>
                    <Text strong className={styles.labelText}>Format:</Text>
                    <Select
                      value={pdfFormat}
                      onChange={(value) => setPdfFormat(value)}
                      className={styles.selectFullWidth}
                      options={[
                        { value: 'memo', label: 'Memo' },
                        { value: 'slide-16:9', label: 'Widescreen (16:9) Slide' },
                        { value: 'slide-4:3', label: 'Standard (4:3) Slide' }
                      ]}
                    />
                  </div>
                  <Button
                    type="primary"
                    size="large"
                    icon={<FilePdfOutlined />}
                    onClick={() => {
                      if (pdfFormat === 'memo') {
                        onExportPDF('memo', '16:9');
                      } else if (pdfFormat === 'slide-16:9') {
                        onExportPDF('slide', '16:9');
                      } else if (pdfFormat === 'slide-4:3') {
                        onExportPDF('slide', '4:3');
                      }
                    }}
                    block
                  >
                    Export PDF
                  </Button>
                  <Text type="secondary" className={styles.exportText}>
                    Download as .pdf document
                  </Text>
                </Space>
              </Card>
            </Col>
          </Row>
        </Space>
      </div>
    </Layout>
  );
};

ReportActions.propTypes = {
  onSetCurrentStep: PropTypes.func.isRequired,
  onNewReport: PropTypes.func.isRequired,
  onExportPowerPoint: PropTypes.func.isRequired,
  onExportPDF: PropTypes.func.isRequired
};

export default ReportActions;
